import { AutoDisableableDto, type AutoDisableableInterface } from '@zaoblako/shared/common/dtos/auto-disableable.dto';
import { PlanningUnitFormDto, type PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { DeadlineFormDto, type DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { entityLikeSchema, entityLikeSchemaUuid } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { DocumentTypeCodeValidateTools } from '@zaoblako/shared/modules/document/classes/document.type.code.validate.tools';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { DocumentCheckersEnum } from '@zaoblako/shared/modules/document/enums/document-checkers.enum';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { DocumentTypeFindInterface } from '@zaoblako/shared/modules/document/dtos/document-type/document-type-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { OperationTypeFindInterface } from '@zaoblako/shared/modules/document/dtos/operation-type/operation-type-find.dto';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';

import * as yup from 'yup';

/** Document template create/update request body - DTO signature */
export interface DocumentTemplateFormInterface extends AutoDisableableInterface {
  /** Template display name */
	name: string;
	/**
	 * Document template amount value. Document created by this template
	 * will have this amount value.
	 */
	amount: number;
	/** Additional commentary to document. Can contain some template variables. */
	commentary?: string;

  // Planning and deadline settings

  /** Planning settings */
  planning: PlanningUnitFormInterface;
  /** Deadline settings */
  deadline: DeadlineFormInterface;

	// Document relations
	/**
	 * Related company. Document created by this template
	 * will have this related company.
	 */
	company: CompanyFindInterface;
	/**
	 * Related counterparty. Document created by this template
	 * will have this related counterparty.
	 */
	contragent: ContragentFindInterface;
	/** Type of document to create. A document with the appropriate type will be created. */
	type: DocumentTypeFindInterface;
  /** Type of operation */
  operationType?: OperationTypeFindInterface;
	/**
	 * Related direction. Document created by this template
	 * will have this related deal.
	 */
  direction?: DirectionFindInterface;
	/**
	 * Related unit. Document created by this template
	 * will have this related deal.
	 */
  unit?: UnitFindInterface;
	/**
	 * Related filial. Document created by this template
	 * will have this related deal.
	 */
  filial?: FilialFindInterface;
	/**
	 * Related deal. Document created by this template
	 * will have this related deal.
	 */
  deal?: DealFindInterface;
  /**
	 * Related cost center. Document created by this template
	 * will have this related cost center.
	 */
  costCenter?: CostCenterFindInterface;
  /**
   * Related currency. Document created by this template
	 * will have this related currency.
   */
  currency?: CurrencyFindInterface;
}

/** Document template create/update request body - DTO schema */
export const DocumentTemplateFormDto = SerdeDtoClass.from<AutoDisableableInterface, DocumentTemplateFormInterface>(
  AutoDisableableDto,
  DocumentDtoKeysEnum.DOCUMENT_TEMPLATE_FORM_DTO,
  {
    name: {
      label: 'Название',
      serializer: yup
        .string()
        .min(1, DtoMessageEnum.EMPTY_STRING),
    },
    amount: {
      label: 'Сумма',
      serializer: yup
        .number()
        .positive(DtoMessageEnum.NOT_POSITIVE),
    },
    commentary: {
      optional: true,
      label: 'Комментарий',
      serializer: yup.string(),
    },
    planning: {
      label: 'Настройки планирования',
      nested: PlanningUnitFormDto,
      serializer: (_, nested) => nested,
    },
    deadline: {
      label: 'Настройки дедлайна',
      nested: DeadlineFormDto,
      serializer: (_, nested) => nested,
    },
    company: {
      label: 'Компания',
      serializer: entityLikeSchema,
    },
    contragent: {
      label: 'Контрагент',
      serializer: entityLikeSchema,
    },
    type: {
      label: 'Тип документа',
      serializer: yup.object({
        code: yup
          .string()
          .matches(
            DocumentTypeCodeValidateTools
              .getInstance()
              .matchRegex,
          ),
      }) as yup.Schema<any>,
    },
    operationType: {
      optional: true,
      label: 'Вид операции',
      serializer: entityLikeSchema,
      checkers: [DocumentCheckersEnum.OPERATION_TYPE_ASSERT_EXISTS_BY_DTO],
    },
    direction: {
      optional: context => !context?.data?.requiredAnalyticalFeatures?.directionsRequired,
      label: 'БС',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
    },
    unit: {
      optional: context => !context?.data?.requiredAnalyticalFeatures?.unitsRequired,
      label: 'БЕ',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
    },
    filial: {
      optional: context => !context?.data?.requiredAnalyticalFeatures?.filialsRequired,
      label: 'Филиал',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
    },
    deal: {
      optional: true,
      label: 'Сделка',
      serializer: entityLikeSchema,
    },
    costCenter: {
      optional: true,
      label: 'Центр затрат',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.COST_CENTER_ASSERT_EXISTS_BY_DTO],
    },
    currency: {
      optional: true,
      label: 'Валюта',
      serializer: entityLikeSchemaUuid,
      checkers: [CatalogCheckersEnum.CURRENCY_ASSERT_EXISTS_BY_DTO],
    },
  },
);
